import { lazy, Suspense, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Sparkles, ShieldCheck, BadgeDollarSign, MessageCircle, ArrowRight, Star, Landmark, Mountain, Utensils } from "lucide-react";
import { useLang } from "@/context/LangContext";
import { useApi } from "@/lib/api";
import DestinationCard from "@/components/site/DestinationCard";
import { TourCard } from "@/components/site/TourCard";
import { CarCard, GuideCard } from "@/components/site/ServiceCards";
import SectionHead from "@/components/site/SectionHead";
import { motion, MotionConfig } from "framer-motion";
import { HeroVideo, HERO_POSTER } from "@/components/site/HeroVideo";
const MapView = lazy(() => import("@/components/site/MapView"));
import BookingDialog from "@/components/site/BookingDialog";
import TikTokHook from "@/components/site/TikTokHook";
import ComingCities from "@/components/site/ComingCities";
import Testimonials from "@/components/site/Testimonials";
import CustomerPhotos from "@/components/site/CustomerPhotos";

const MOODS = [["culture", Landmark], ["nature", Mountain], ["culinary", Utensils]];

function Hero() {
  const { t } = useLang();
  const [reducedMotion, setReducedMotion] = useState(() => window.matchMedia("(prefers-reduced-motion: reduce)").matches);
  useEffect(() => {
    const media = window.matchMedia("(prefers-reduced-motion: reduce)");
    const update = () => setReducedMotion(media.matches);
    media.addEventListener("change", update);
    return () => media.removeEventListener("change", update);
  }, []);
  const entrance = (delay = 0) => ({ initial: reducedMotion ? false : { opacity: 0, y: 12 }, animate: { opacity: 1, y: 0 }, transition: { duration: reducedMotion ? 0 : 0.45, delay: reducedMotion ? 0 : delay, ease: "easeOut" } });
  const scrollCleanup = useRef(() => {});
  useEffect(() => () => scrollCleanup.current(), []);
  const goToPlanner = () => {
    scrollCleanup.current();
    const heading = document.getElementById("home-ai-heading");
    const header = document.querySelector(".site-header");
    const targetTop = () => window.scrollY + heading.getBoundingClientRect().top - header.getBoundingClientRect().height - 16;
    // Correct once if late catalog/media layout shifts move the destination.
    // Any fresh user scroll/key gesture cancels this correction.
    let timer;
    const cleanup = () => {
      clearTimeout(timer);
      window.removeEventListener("scrollend", correct);
      window.removeEventListener("scroll", settled);
      for (const event of ["wheel", "touchstart", "keydown"]) window.removeEventListener(event, cleanup, true);
    };
    const correct = () => {
      cleanup();
      if (Math.abs(heading.getBoundingClientRect().top - header.getBoundingClientRect().height - 16) > 3) {
        window.scrollTo({ top: targetTop(), behavior: "instant" });
      }
    };
    // Fallback for browsers without scrollend: wait for scroll activity to settle,
    // rather than assuming a long smooth scroll finishes in a fixed duration.
    const settled = () => { clearTimeout(timer); timer = setTimeout(correct, 220); };
    scrollCleanup.current = cleanup;
    window.addEventListener("scrollend", correct, { once: true });
    window.addEventListener("scroll", settled, { passive: true });
    for (const event of ["wheel", "touchstart", "keydown"]) window.addEventListener(event, cleanup, { once: true, passive: true, capture: true });
    settled();
    heading.focus({ preventScroll: true });
    window.scrollTo({ top: targetTop(), behavior: reducedMotion ? "instant" : "smooth" });
  };
  return (
    <section className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 pt-4">
      <MotionConfig reducedMotion={reducedMotion ? "always" : "never"}>
      <div data-testid="cinematic-hero" className="home-hero cinematic-hero relative isolate flex flex-col overflow-hidden rounded-[2rem] bg-[#022C22] grain">
        <img src={HERO_POSTER} data-testid="hero-video-poster" width={1280} height={720} loading="eager" fetchPriority="high" decoding="async" alt={t("hero_landscape_alt")} className="absolute inset-0 h-full w-full object-cover" />
        <div className="hero-video-shade pointer-events-none absolute inset-0 z-[1]" aria-hidden="true" />
        <div className="hero-copy relative z-10 flex w-full flex-1 flex-col items-center justify-center text-center">
          <motion.p {...entrance()} className="hero-kicker mb-5 inline-flex max-w-full items-center justify-center gap-2 text-xs font-bold uppercase tracking-[0.16em] text-amber-200"><Star aria-hidden="true" className="h-4 w-4 shrink-0 fill-amber-200" />{t("hero_kicker")}</motion.p>
          <motion.h1 {...entrance(0.08)} data-testid="hero-vibe-headline" className="cinematic-greeting max-w-4xl text-white">
            <span className="hero-place block font-display font-semibold tracking-tight">{t("hero_title_1")}</span>{" "}<span className="hero-prompt mx-auto mt-4 block max-w-3xl font-medium leading-snug text-amber-100">{t("hero_title_2")}</span>
          </motion.h1>
          <motion.div role="group" aria-label="Interactive Vibe Matcher" data-testid="hero-vibe-matcher" className="mt-8 flex w-full max-w-3xl flex-wrap justify-center gap-3">
            {MOODS.map(([mood, Icon], index) => <motion.button key={mood} {...entrance(0.16 + index * 0.07)} type="button" data-testid={`hero-vibe-pill-${mood}`} aria-controls="home-ai-trip-planner-section" onClick={goToPlanner}
              whileHover={reducedMotion ? { backgroundColor: "#064E3B" } : { y: -3, backgroundColor: "#064E3B" }} whileFocus={{ borderColor: "#FCD34D", backgroundColor: "#064E3B" }} whileTap={reducedMotion ? { backgroundColor: "#92400E" } : { scale: 0.97, backgroundColor: "#92400E" }}
              className="vibe-pill flex min-h-12 min-w-11 max-w-full items-center justify-center gap-2 rounded-full border border-amber-200/50 bg-[#022C22]/75 px-5 py-3 text-center text-sm font-semibold text-white shadow-lg backdrop-blur-md focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-amber-300 sm:text-base">
              <Icon aria-hidden="true" className="h-5 w-5 shrink-0 text-amber-200" /><span className="min-w-0 [overflow-wrap:anywhere]">{t(`vibe_${mood}`)}</span>
            </motion.button>)}
          </motion.div>
          <motion.p {...entrance(0.35)} className="hero-description mt-5 max-w-xl text-sm leading-relaxed text-white/85">{t("hero_sub")}</motion.p>
        </div>
        <HeroVideo reducedMotion={reducedMotion} />
      </div>
      </MotionConfig>
      <div className="home-trust grid grid-cols-2 lg:grid-cols-4 gap-4 py-8">
        {[["25.000+", "trust_travellers"], ["4.9/5", "trust_rating"], ["120+", "trust_drivers"], ["< 5 min", "trust_response"]].map(([v, k]) => (
          <div key={k} className="rounded-2xl border border-slate-200 bg-white px-5 py-4"><p className="text-2xl font-extrabold text-[#064E3B]">{v}</p><p className="text-sm text-slate-500 [overflow-wrap:anywhere]">{t(k)}</p></div>
        ))}
      </div>
    </section>
  );
}

function AiBanner() {
  const { t } = useLang();
  return (
    <section id="home-ai-trip-planner-section" data-testid="home-ai-trip-planner-section" aria-labelledby="home-ai-heading" className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-12">
      <div className="home-ai-surface relative overflow-hidden rounded-[2rem] bg-[#064E3B] p-8 sm:p-12 lg:p-16 grain">
        <div className="absolute -right-24 -top-24 h-80 w-80 rounded-full bg-[#E11D48]/30 blur-3xl" />
        <div className="relative grid gap-8 lg:grid-cols-2 items-center">
          <div className="ai-banner-copy">
            <p className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-[0.2em] text-amber-300"><Sparkles className="h-4 w-4" />AI Trip Planner</p>
            <h2 id="home-ai-heading" data-testid="home-ai-heading" tabIndex={-1} className="mt-3 text-3xl sm:text-4xl font-bold tracking-tight text-white outline-none">{t("sec_ai")}</h2>
            <p className="ai-banner-description mt-3 text-emerald-50/80">{t("sec_ai_sub")}</p>
            <Link to="/planner" data-testid="home-try-ai-btn" className="mt-6 inline-flex min-h-11 max-w-full items-center gap-2 rounded-full bg-white px-6 py-3 font-bold text-[#064E3B] transition-transform hover:scale-[1.03] [overflow-wrap:anywhere]">{t("try_ai")}<ArrowRight className="h-4 w-4" /></Link>
          </div>
          <div className="space-y-3">
            {[["08:00", "Kelok 44 → Danau Maninjau"], ["12:30", "Nasi Kapau Uni Lis, Bukittinggi"], ["15:00", "Ngarai Sianok & Lobang Jepang"], ["19:00", "Jam Gadang Night Walk"]].map(([tm, a], i) => (
              <div key={tm} className="flex items-center gap-4 rounded-2xl bg-white/10 px-5 py-3 text-white backdrop-blur fade-up" style={{ animationDelay: `${i * 120}ms` }}>
                <span className="shrink-0 whitespace-nowrap font-mono text-sm text-amber-300">{tm}</span><span className="font-semibold">{a}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

const Why = () => {
  const { t } = useLang();
  const items = [[ShieldCheck, "why_1"], [BadgeDollarSign, "why_2"], [MessageCircle, "why_3"]];
  return (
    <section className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-12">
      <h2 className="text-2xl sm:text-3xl font-bold tracking-tight mb-8">{t("why_title")}</h2>
      <div className="grid gap-6 md:grid-cols-3">
        {items.map(([Icon, k]) => (
          <div key={k} className="home-why-card min-w-0 rounded-3xl bg-white border border-slate-200 p-7 [overflow-wrap:anywhere]">
            <Icon className="h-9 w-9 text-[#E11D48]" /><h3 className="mt-4 text-lg font-bold">{t(k)}</h3><p className="mt-1 text-slate-500">{t(k + "d")}</p>
          </div>
        ))}
      </div>
    </section>
  );
};

function HomeMap({ points }) {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const observer = new IntersectionObserver(([entry]) => { if (entry.isIntersecting) { setVisible(true); observer.disconnect(); } }, { rootMargin: "200px" });
    observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);
  return <div ref={ref} className="min-h-[460px]" data-testid="home-deferred-map">{visible && <Suspense fallback={<div className="h-[460px] rounded-3xl bg-emerald-50" />}><MapView points={points} height="h-[460px]" /></Suspense>}</div>;
}

export default function Home() {
  const { t } = useLang();
  const dest = useApi("/destinations");
  const tours = useApi("/tours");
  const cars = useApi("/cars");
  const guides = useApi("/guides");
  const [booking, setBooking] = useState(null);
  const points = (dest.data || []).map((d) => ({ ...d, subtitle: d.city }));
  return (
    <div data-testid="home-page">
      <Hero />
      <TikTokHook />
      <section className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-12">
        <SectionHead title={t("sec_top_dest")} sub={t("sec_top_dest_sub")} to="/destinations" />
        <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4">{(dest.data || []).slice(0, 8).map((d, i) => <DestinationCard key={d.slug} d={d} i={i} />)}</div>
      </section>
      <section className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-12">
        <SectionHead title={t("sec_tours")} sub={t("sec_tours_sub")} to="/tours" />
        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">{(tours.data || []).slice(0, 3).map((tr, i) => <TourCard key={tr.slug} tour={tr} i={i} onBook={setBooking} />)}</div>
      </section>
      <AiBanner />
      <section className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-12">
        <SectionHead title={t("sec_map")} sub={t("sec_map_sub")} to="/map" />
        <HomeMap points={points} />
      </section>
      <section className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-12">
        <SectionHead title={t("sec_cars")} to="/cars" />
        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">{(cars.data || []).slice(0, 3).map((c) => <CarCard key={c.slug} car={c} onBook={setBooking} />)}</div>
      </section>
      <section className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-12">
        <SectionHead title={t("sec_guides")} sub={t("sec_guides_sub")} to="/guides" />
        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-4">{(guides.data || []).map((g) => <GuideCard key={g.slug} g={g} onBook={setBooking} />)}</div>
      </section>
      <Testimonials />
      <CustomerPhotos />
      <Why />
      <ComingCities />
      {booking && <BookingDialog key={booking.type + booking.slug} item={booking} onClose={() => setBooking(null)} />}
    </div>
  );
}
